import { Link } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { TERMS_PAGE_SEO } from "@/constants/seoConfig";

export default function TermsPage() {
  const termsSeo = useMemo(() => TERMS_PAGE_SEO, []);
  useSEO(termsSeo);
  return (
    <div className="min-h-screen">
      <Navbar />
      <main>
        <section
          className="pt-28 pb-10 px-4 md:px-8 relative overflow-hidden"
          style={{ background: "linear-gradient(160deg, #f5f0ff 0%, #ede8ff 60%, #faf8ff 100%)" }}
        >
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute w-96 h-96 rounded-full opacity-20 -top-20 -right-20" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 70%)" }} />
            <div className="absolute w-64 h-64 rounded-full opacity-15 bottom-0 left-10" style={{ background: "radial-gradient(circle, #7C3AED 0%, transparent 70%)" }} />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <nav aria-label="Breadcrumb" className="flex justify-center mb-4">
              <ol className="flex items-center gap-2 text-sm text-[#7a6aaa]">
                <li><Link to="/" className="hover:text-[#7C3AED] transition-colors">Home</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-[#7C3AED] font-medium">Terms of Service</li>
              </ol>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#7C3AED]/20 bg-white text-[#7C3AED] text-sm font-medium mb-4">
              <i className="ri-file-list-3-line"></i>
              Legal
            </div>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-[#1a1040] mb-3 leading-tight">
              Terms of Service
            </h1>
            <p className="text-[#5a4a8a] text-base max-w-xl mx-auto">
              Last updated: April 28, 2026
            </p>
          </div>
        </section>

        {/* Blend: gradient to white */}
        <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #ffffff)" }} />

        <div className="bg-white">
          <div className="max-w-3xl mx-auto px-4 md:px-8 py-14">
            <div className="space-y-8 text-[#5a4a8a] text-sm leading-relaxed">
              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">1. Acceptance of Terms</h2>
                <p>
                  By downloading, installing, or using the Ava Chat AI application ("Ava," "we," "us," or "our"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use our services.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">2. Description of Service</h2>
                <p>
                  Ava is an AI-powered companion application designed to provide emotional support, conversation, and companionship. Ava is not a medical device, diagnostic tool, or substitute for professional mental health care, therapy, or medical advice.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">3. Eligibility</h2>
                <p>
                  You must be at least 13 years of age to use Ava. If you are under 18, you must have permission from a parent or guardian. By using Ava, you represent that you meet these eligibility requirements.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">4. Not Medical Advice</h2>
                <p className="mb-2">
                  <strong className="text-[#1a1040]">Important Disclaimer:</strong> Ava is an AI companion, not a healthcare provider. The conversations, suggestions, and support provided by Ava are for informational and companionship purposes only.
                </p>
                <p>
                  If you are experiencing a mental health crisis, suicidal thoughts, or any emergency, please contact emergency services immediately or reach out to a licensed mental health professional. In the United States, call or text 988 for the Suicide & Crisis Lifeline.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">5. User Accounts and Subscriptions</h2>
                <p className="mb-2">
                  Ava offers both free and paid subscription plans. Free users receive up to 10 chats per day. Paid subscriptions provide unlimited chats, priority responses, and additional features.
                </p>
                <p>
                  Subscription fees are billed in advance on a monthly or yearly basis. You may cancel your subscription at any time through your app store account settings. Refunds are handled in accordance with the policies of the respective app store (Apple App Store or Google Play Store).
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">6. Privacy and Data</h2>
                <p>
                  Your privacy is important to us. Please review our <Link to="/privacy-policy" className="text-[#7C3AED] hover:underline">Privacy Policy</Link> to understand how we collect, use, and protect your personal information. We do not sell your data to third parties.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">7. Acceptable Use</h2>
                <p className="mb-2">You agree not to use Ava to:</p>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  <li>Harass, abuse, or harm others</li>
                  <li>Generate or distribute illegal content</li>
                  <li>Attempt to reverse engineer or exploit the application</li>
                  <li>Use automated systems or bots to interact with Ava</li>
                  <li>Violate any applicable laws or regulations</li>
                </ul>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">8. Intellectual Property</h2>
                <p>
                  All content, trademarks, logos, and intellectual property related to Ava are owned by Neuroava LLC. You may not copy, modify, distribute, or create derivative works without our express written permission.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">9. Limitation of Liability</h2>
                <p>
                  To the maximum extent permitted by law, Neuroava LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of Ava. Our total liability shall not exceed the amount you paid for the service in the 12 months preceding the claim.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">10. Changes to Terms</h2>
                <p>
                  We may update these Terms of Service from time to time. We will notify you of any material changes through the app or via email. Your continued use of Ava after changes constitutes acceptance of the updated terms.
                </p>
              </section>

              <section>
                <h2 className="font-display text-lg font-bold text-[#1a1040] mb-3">11. Contact Information</h2>
                <p className="mb-2">If you have any questions about these Terms of Service, please contact us:</p>
                <div className="bg-white rounded-xl p-5 border border-[#e8e0ff] mt-3">
                  <p className="mb-1"><strong className="text-[#1a1040]">Neuroava LLC</strong></p>
                  <p className="mb-1">United States</p>
                  <p className="mb-1">Email: <a href="mailto:info@neuroava.com" className="text-[#7C3AED] hover:underline">info@neuroava.com</a></p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      {/* Blend: white to gradient */}
      <div className="h-12 -mt-12 relative z-10 pointer-events-none" style={{ background: "linear-gradient(to bottom, transparent, #f5f0ff)" }} />
      <Footer />
    </div>
  );
}